import { useState, useCallback, useRef } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useToast } from '@/hooks/use-toast';
import { sanitizeInput, isRateLimited, logSecurityEvent, ValidationResult, validateAndSanitize, strictPinSchema, lenientPinSchema, emailSchema, verificationCodeSchema, sanitizePinInput, sanitizeEmail } from '@/lib/security-validation';

// Module-scope cache for check_lockout calls to prevent duplicates
let checkLockoutCache: {
  promise: Promise<any> | null;
  timestamp: number;
  result: any;
} = { promise: null, timestamp: 0, result: null };

const CACHE_TTL = 10000; // 10 seconds

interface PinStatus {
  isLockedOut: boolean;
  lockoutRemaining: number;
  attemptsRemaining: number;
  needsOnboarding: boolean;
  hasPin: boolean;
  requiresPinChange: boolean;
}

interface UsePinManagementReturn {
  verifyPin: (pin: string) => Promise<{ success: boolean; error?: string; requiresPinChange?: boolean }>;
  requestPinChange: (currentPin: string, newPin: string, confirmNewPin: string, userEmail: string) => Promise<{ success: boolean; error?: string }>;
  confirmPinChange: (verificationCode: string) => Promise<{ success: boolean; error?: string }>;
  requestPinReset: (userEmail: string) => Promise<{ success: boolean; error?: string }>;
  confirmPinReset: (verificationCode: string, newPin: string, confirmNewPin: string) => Promise<{ success: boolean; error?: string }>;
  checkLockoutStatus: () => Promise<PinStatus>;
  isLoading: boolean;
}

export const usePinManagement = (): UsePinManagementReturn => {
  const { user } = useUser();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const callPinApi = useCallback(async (action: string, data: any) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Skip client-side rate limiting for check_lockout (safe read operation)
    if (action !== 'check_lockout') {
      const rateLimitKey = `pin_${action}_${user.id}`;
      if (isRateLimited(rateLimitKey, action === 'verify_pin' ? 10 : 3, 60000)) {
        logSecurityEvent('rate_limit_exceeded', { action, userId: user.id });
        throw new Error('Too many attempts. Please wait before trying again.');
      }
    }

    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/pin-management`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`
      },
      body: JSON.stringify({
        action,
        clerkUserId: user.id,
        ...data
      })
    });

    const result = await response.json();
    
    if (!response.ok) {
      // Log security events for certain errors
      if (response.status === 429) {
        logSecurityEvent('server_rate_limit_hit', { action, status: response.status });
      } else if (response.status >= 400) {
        logSecurityEvent('api_error', { action, status: response.status, error: result.error });
      }
      
      throw new Error(result.error || 'API request failed');
    }

    return result;
  }, [user]);

  const verifyPin = useCallback(async (pin: string) => {
    setIsLoading(true);
    try {
      // Use lenient validation for current PIN verification (allows weak PINs)
      const validation: ValidationResult = validateAndSanitize(pin, lenientPinSchema, sanitizePinInput);
      
      if (!validation.isValid) {
        logSecurityEvent('invalid_pin_format', { error: validation.error });
        return { success: false, error: validation.error || 'Invalid PIN format' };
      }

      const result = await callPinApi('verify_pin', { pin: validation.sanitizedValue });
      
      if (result.valid) {
        return { success: true, requiresPinChange: result.requiresPinChange };
      } else {
        const errorMessage = result.isLockedOut 
          ? 'Account temporarily locked due to failed attempts'
          : `Incorrect PIN. ${result.attemptsRemaining || 0} attempts remaining`;
        
        return { success: false, error: errorMessage };
      }
    } catch (error: any) {
      logSecurityEvent('pin_verification_error', { error: error.message });
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  }, [callPinApi]);

  const requestPinChange = useCallback(async (
    currentPin: string, 
    newPin: string, 
    confirmNewPin: string, 
    userEmail: string
  ) => {
    setIsLoading(true);
    try {
      // Validate and sanitize all inputs
      const currentPinValidation = validateAndSanitize(currentPin, lenientPinSchema, sanitizePinInput); // Allow weak current PIN
      const newPinValidation = validateAndSanitize(newPin, strictPinSchema, sanitizePinInput); // Require strong new PIN
      const emailValidation = validateAndSanitize(userEmail, emailSchema, sanitizeEmail);

      if (!currentPinValidation.isValid) {
        return { success: false, error: `Current PIN: ${currentPinValidation.error}` };
      }

      if (!newPinValidation.isValid) {
        return { success: false, error: `New PIN: ${newPinValidation.error}` };
      }

      if (!emailValidation.isValid) {
        return { success: false, error: `Email: ${emailValidation.error}` };
      }

      if (newPin !== confirmNewPin) {
        return { success: false, error: 'New PIN confirmation does not match' };
      }

      if (currentPin === newPin) {
        return { success: false, error: 'New PIN must be different from current PIN' };
      }

      await callPinApi('request_pin_change', {
        currentPin: currentPinValidation.sanitizedValue,
        newPin: newPinValidation.sanitizedValue,
        confirmNewPin: newPinValidation.sanitizedValue, // Use same sanitized value
        userEmail: emailValidation.sanitizedValue
      });

      toast({
        title: "Verification Code Sent",
        description: "Check your email for the verification code to complete the PIN change.",
      });

      return { success: true };
    } catch (error: any) {
      toast({
        title: "PIN Change Request Failed",
        description: error.message,
        variant: "destructive"
      });
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  }, [callPinApi, toast]);

  const confirmPinChange = useCallback(async (verificationCode: string) => {
    setIsLoading(true);
    try {
      // Validate and sanitize verification code
      const validation = validateAndSanitize(verificationCode, verificationCodeSchema, sanitizePinInput);
      
      if (!validation.isValid) {
        return { success: false, error: validation.error || 'Invalid verification code format' };
      }

      await callPinApi('confirm_pin_change', { verificationCode: validation.sanitizedValue });

      toast({
        title: "PIN Changed Successfully",
        description: "Your PIN has been updated.",
      });

      return { success: true };
    } catch (error: any) {
      toast({
        title: "PIN Change Failed",
        description: error.message,
        variant: "destructive"
      });
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  }, [callPinApi, toast]);

  const checkLockoutStatus = useCallback(async (): Promise<PinStatus> => {
    // Check cache first to avoid duplicate calls
    const now = Date.now();
    if (checkLockoutCache.result && (now - checkLockoutCache.timestamp) < CACHE_TTL) {
      return checkLockoutCache.result;
    }

    // If there's an in-flight promise, wait for it
    if (checkLockoutCache.promise) {
      return await checkLockoutCache.promise;
    }

    // Create new promise and cache it
    checkLockoutCache.promise = (async () => {
      try {
        const result = await callPinApi('check_lockout', {});
        const status = {
          isLockedOut: result.isLockedOut || false,
          lockoutRemaining: result.lockoutRemaining || 0,
          attemptsRemaining: result.attemptsRemaining || 3,
          needsOnboarding: result.needsOnboarding || false,
          hasPin: result.hasPin || false,
          requiresPinChange: result.requiresPinChange || false
        };
        
        // Cache the result
        checkLockoutCache.result = status;
        checkLockoutCache.timestamp = Date.now();
        
        return status;
      } catch (error: any) {
        // If user not found, they need onboarding
        if (error.message.includes('User not found')) {
          const status = {
            isLockedOut: false,
            lockoutRemaining: 0,
            attemptsRemaining: 3,
            needsOnboarding: true,
            hasPin: false,
            requiresPinChange: false
          };
          checkLockoutCache.result = status;
          checkLockoutCache.timestamp = Date.now();
          return status;
        }
        throw error;
      } finally {
        // Clear the promise reference
        checkLockoutCache.promise = null;
      }
    })();

    return await checkLockoutCache.promise;
  }, [callPinApi]);

  const requestPinReset = useCallback(async (userEmail: string) => {
    setIsLoading(true);
    try {
      const emailValidation = validateAndSanitize(userEmail, emailSchema, sanitizeEmail);

      if (!emailValidation.isValid) {
        return { success: false, error: `Email: ${emailValidation.error}` };
      }

      await callPinApi('request_pin_reset', {
        userEmail: emailValidation.sanitizedValue
      });

      toast({
        title: "PIN Reset Code Sent",
        description: "Check your email for the verification code to reset your PIN.",
      });

      return { success: true };
    } catch (error: any) {
      toast({
        title: "PIN Reset Request Failed",
        description: error.message,
        variant: "destructive"
      });
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  }, [callPinApi, toast]);

  const confirmPinReset = useCallback(async (verificationCode: string, newPin: string, confirmNewPin: string) => {
    setIsLoading(true);
    try {
      const codeValidation = validateAndSanitize(verificationCode, verificationCodeSchema, sanitizePinInput);
      const newPinValidation = validateAndSanitize(newPin, strictPinSchema, sanitizePinInput);
      
      if (!codeValidation.isValid) {
        return { success: false, error: codeValidation.error || 'Invalid verification code format' };
      }

      if (!newPinValidation.isValid) {
        return { success: false, error: `New PIN: ${newPinValidation.error}` };
      }

      if (newPin !== confirmNewPin) {
        return { success: false, error: 'PIN confirmation does not match' };
      }

      await callPinApi('confirm_pin_reset', { 
        verificationCode: codeValidation.sanitizedValue,
        newPin: newPinValidation.sanitizedValue,
        confirmNewPin: newPinValidation.sanitizedValue
      });

      toast({
        title: "PIN Reset Successfully",
        description: "Your PIN has been reset.",
      });

      return { success: true };
    } catch (error: any) {
      toast({
        title: "PIN Reset Failed",
        description: error.message,
        variant: "destructive"
      });
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  }, [callPinApi, toast]);

  return {
    verifyPin,
    requestPinChange,
    confirmPinChange,
    requestPinReset,
    confirmPinReset,
    checkLockoutStatus,
    isLoading
  };
};